import { Direction, Displaying } from "@bonusx-global/bx-design-system";
import Box from "@mui/material/Box";
import type { Variant } from "@mui/material/styles/createTypography";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { EMPTY_STRING } from "../../../../constants/commonStrings";
import BonusXLoading from "../../BonusXLoading";
import AiChatbotSuggestedPrompt from "../ai-chatbot-suggested-prompt";
import AiChatbotSuggestedPromptChatHeader from "../components/ai-chatbot-suggested-prompt-chat-header";
import AiChatbotSuggestedPromptPageHeader from "../components/ai-chatbot-suggested-prompt-page-header";
import { useAiChatbotCmsPrompt } from "../../../../api/features/ai-chatbot-cms-prompt/useAiChatbotCmsPrompt.query";
import type { ClaraEntryPoint } from "../../../../features/ai-chatbot/constants/clara-entry-point.enum";
import { FetchStrategies } from "../../../../features/ai-chatbot/constants/fetch-strategies.enum";

interface AiChatbotSuggestedPromptWrapperProps {
  fetchStrategy: FetchStrategies;
  title?: string;
  hasLastChatButton?: boolean;
  onClickLastChatButton?: () => void;
  flexDirection: Direction;
  wrapperComponent?: React.ComponentType<{ children: React.ReactNode }>;
  eventNameToFireOnClick?: string;
  titleVariant?: Variant;
  entryPoint?: ClaraEntryPoint;
}

function AiChatbotSuggestedPromptWrapper({
  title,
  hasLastChatButton = false,
  onClickLastChatButton,
  fetchStrategy,
  flexDirection,
  wrapperComponent: WrapperComponent,
  eventNameToFireOnClick,
  titleVariant,
  entryPoint,
}: Readonly<AiChatbotSuggestedPromptWrapperProps>): JSX.Element {
  const location = useLocation();
  const { suggestedPrompts, isLoading } = useAiChatbotCmsPrompt(fetchStrategy);

  const isVariantChat = fetchStrategy === FetchStrategies.inChat;

  const suggestedPromptsFiltered = useMemo(
    function memoizeSuggestedPrompts() {
      if (fetchStrategy === FetchStrategies.inChat) {
        return suggestedPrompts;
      }

      const filteredPrompts = suggestedPrompts.filter(function filterPromptsByLocation(suggestedPrompt) {
        if (!suggestedPrompt.aiPromptPath) {
          return false;
        }

        const matchedPrompt = matchPath({ path: suggestedPrompt.aiPromptPath, end: true }, location.pathname);

        return matchedPrompt;
      });

      return filteredPrompts;
    },
    [fetchStrategy, suggestedPrompts, location.pathname]
  );

  if (isLoading) {
    return <BonusXLoading />;
  }

  if (isEmpty(suggestedPromptsFiltered)) {
    return <></>;
  }

  const content = (
    <Box
      id="product-page-detail-suggested-prompts-container"
      display={Displaying.flex}
      flexDirection={Direction.column}
    >
      {isVariantChat ? (
        <AiChatbotSuggestedPromptChatHeader />
      ) : (
        <AiChatbotSuggestedPromptPageHeader title={title ?? EMPTY_STRING} titleVariant={titleVariant} />
      )}

      <AiChatbotSuggestedPrompt
        flexDirection={flexDirection}
        promptStrategy={fetchStrategy}
        hasLastChatButton={hasLastChatButton}
        onClickLastChatButton={onClickLastChatButton}
        suggestedPrompts={suggestedPromptsFiltered}
        eventNameToFireOnClick={eventNameToFireOnClick}
        entryPoint={entryPoint}
      />
    </Box>
  );

  if (WrapperComponent) {
    return <WrapperComponent>{content}</WrapperComponent>;
  }

  return content;
}

export default AiChatbotSuggestedPromptWrapper;
